export const DEFAULT_REFERENCE_TITLE = "Default Config";

export const PUBLISHED_REFERENCE_TITLE = "Published Version";

export const PUBLISHED_REFERENCE_DESCRIPTION = "What end users are running right now. Read-only.";

export const READ_ONLY_TITLE = "Running on";

export const DEFAULT_REFERENCE_DESCRIPTION = "The system default this language started from. Read-only.";

export const READ_ONLY_DESCRIPTION =
  "Read-only: this is the version end users are running. To make changes, open this prompt from the working copy on the dashboard — or create one in Language Management.";

export const SAVE_WRITES_TO_DRAFT = "Saving writes to this language's working copy, not to what end users run.";

export const REFERENCE_ABSENT = "Nothing to compare against yet.";

export const INHERITED_SETTING = "inherits the default";

export const DIFF_IDENTICAL = "The draft is identical to this copy.";

export const DIFF_CALLOUT_TITLE = "Your draft differs from this copy";

export const DIFF_ADDED_LINES = (lines: number) => `+${lines} line${lines === 1 ? "" : "s"}`;

export const DIFF_REMOVED_LINES = (lines: number) => `−${lines} line${lines === 1 ? "" : "s"}`;

export const DIFF_VIEW_CHANGES = "View changes";

export const DISCARD_BUTTON = "Discard Changes";

export const DISCARD_TITLE = "Discard unsaved changes?";

export const DISCARD_MESSAGE =
  "The text, model and temperature go back to what was last saved for this prompt. Anything typed since then will be gone. What is already saved stays saved.";

export const DISCARD_OK_TEXT = "Discard";

export const DISCARD_SUCCESS = "Unsaved changes discarded.";

export const REFERENCE_VIEW_TEXT = "View full text";

export const REFERENCE_TAB_CHANGES = "Changes";

export const REFERENCE_TAB_TEXT = "Full text";

export const DIFF_LIVE_NOTE =
  "This comparison updates as you type: anything you change in the editor shows up here, before you save.";

export const DIFF_TRUNCATED =
  "This prompt is too long to line up line by line, so both copies are listed in full instead.";

export const DICTIONARY_TITLE = "Variable Dictionary";

export const DICTIONARY_DESCRIPTION = "Values the app fills in when it runs this prompt.";

export const REQUIRED_VARIABLES_TITLE = "Required";

export const OPTIONAL_VARIABLES_TITLE = "Optional";

export const INSERT_VARIABLE_HINT = "Insert at the cursor";

export const NO_VARIABLES = "This prompt takes no variables.";

export const PROMPT_LOADING = "Loading this prompt";

export const PROMPT_NOT_FOUND = "This prompt is not part of the selected language.";

export const TEMPERATURE_LABEL = "Temperature";

export const TEMPERATURE_STRICT = "Strict";

export const TEMPERATURE_CREATIVE = "Creative";

export const EFFORT_LABEL = "Reasoning effort";

export const MODEL_COST_BUTTON = "Model Cost";

/**
 * Marks a retiring model in the picker's list, inside a 200px trigger.
 *
 * "deprecated" named the vendor's status, which in a list of choices reads as "not selectable" — and
 * it is selectable. "retiring" says the same fact as a direction of travel, so the option looks like
 * what it is: available, with an end coming. The date does not fit here; the dialog states it the
 * moment the option is chosen.
 */
export const MODEL_DEPRECATED_SUFFIX = "retiring";

/**
 * What the read-only bar says about a deprecated model's remaining life — see `modelAvailability`,
 * which is what joins either of these to a date.
 *
 * Phrased as the availability rather than the status because the status was being misread: a
 * deprecated model still runs, and the prompts pointed at it are not broken. Leading with
 * "Available" states that, and demotes the date to the condition it actually is.
 */
export const MODEL_AVAILABLE_UNTIL = "Available until";

/** For a deprecated model whose vendor announced no shutdown date — most of them. */
export const MODEL_AVAILABLE_NO_DATE = "Available, no longer recommended";

/** The grid's cell holds the model name and the effort already, so there it says only the date. */
export const MODEL_AVAILABLE_UNTIL_SHORT = "until";

/** Dateless and in a cell: the mildest word that is still true. Not "deprecated". */
export const MODEL_AVAILABLE_NO_DATE_SHORT = "ageing";

/**
 * Past the shutdown date — where "Available until" would be a plain lie.
 *
 * Nothing removes a model from the picker on its date: the list comes from the backend's
 * SUPPORTED_LLM_MODELS, so the row keeps arriving with a date now in the past until someone edits
 * that constant. A prompt still pointed here is one whose calls the vendor has stopped answering,
 * which is the one state in this feature that IS something to fix.
 */
export const MODEL_RETIRED_ON = "Retired on";

/** What follows the date, so the line does not stop at a date that could read as "until". */
export const MODEL_RETIRED_SUFFIX = "— no longer served";

/** The grid's cell, where the name and the effort are already competing for the width. */
export const MODEL_RETIRED_ON_SHORT = "retired";

export const MODEL_DEPRECATED_FALLBACK_NOTE = "It still runs, but new configurations should not be pointed at it.";

/**
 * The dialog shown when an operator picks a retiring model, and the words are the whole point of it.
 *
 * NAMES THE CONSEQUENCE, NOT THE VENDOR'S LABEL. "Use a Deprecated Model?" asked about a status, and
 * the status is the part an operator cannot act on — it also implied the choice was forbidden, when a
 * retiring model is a legitimate pick with an expiry. This asks about the thing that will actually
 * happen: the prompt stops working on a date. `modelRetirementWarning` supplies that date.
 */
export const MODEL_DEPRECATED_TITLE = "This Model Is Being Retired";

/** Not "Anyway": there is nothing to override. Choosing it is allowed, and the date is the caveat. */
export const MODEL_DEPRECATED_OK_TEXT = "Use This Model";

/**
 * The question, which NAMES THE MODEL rather than saying "it".
 *
 * "Point this prompt at it?" made the reader carry the subject down from the warning line — and the
 * line between them is vendor prose that routinely names a DIFFERENT id (the dated snapshot behind
 * an alias), so "it" was genuinely ambiguous. `modelRetirementQuestion` joins this to the name.
 */
export const MODEL_DEPRECATED_QUESTION_PREFIX = "Point this prompt at";

/** Kept: the operator IS overriding our advice here, even though the choice is allowed. */
export const MODEL_DEPRECATED_QUESTION_SUFFIX = "anyway?";

/**
 * What to use instead, from the vendor's own successor list.
 *
 * THE ACTIONABLE HALF, and it was being dropped. `deprecation_note` is extractor output and often
 * explains how the catalogue reached its verdict rather than what to do — one real note reads "the
 * document lists gpt-4.1-mini-2025-04-14 as deprecated and says fine-tuned versions follow the same
 * shutdown schedule as their underlying base models", which names an id we do not configure and a
 * feature we do not use. The successor list answers the operator's actual question.
 */
export const MODEL_SUCCESSOR_PREFIX = "The vendor recommends";

export const MODEL_SUCCESSOR_SUFFIX = "instead.";

/** Leads the dialog, with the date the vendor announced. See `modelRetirementWarning`. */
export const MODEL_RETIRING_ON = "stops working on";

/** The same sentence for a model the vendor is retiring without naming a date — most of them. */
export const MODEL_RETIRING_NO_DATE = "is being retired, with no end date announced yet";

export const MODEL_COST_TITLE = "Model Cost";

export const MODEL_COST_DESCRIPTION = "Current prices per million tokens.";

export const MODEL_COST_DEFAULT = "Default model";

export const COST_UNKNOWN = "No data";

export const COST_NOT_PRICED = "Not priced here";

export const COST_CACHE_WRITE_AS_INPUT = "As input";

export const CHANGE_SETTING_CURRENT = "Current:";

export const CHANGE_SETTING_SWITCH_TO = "Switch to:";

export interface ChangeSettingCopy {
  title: string;
  affects: (count: number) => string;
  question: string;
  okText: string;
}

const prompts = (count: number) => `${count} ${count === 1 ? "prompt" : "prompts"}`;

export const CHANGE_SETTING_COPY: Record<"model" | "temperature" | "effort", ChangeSettingCopy> = {
  model: {
    title: "Change AI Model & Review Affected Prompts?",
    affects: (count) => `Switching models will affect the execution behavior of the following ${prompts(count)}:`,
    question: "Would you like to proceed with changing the model and review/edit these prompts now?",
    okText: "Switch Model & Review Prompts",
  },
  temperature: {
    title: "Change Temperature & Review Affected Prompts?",
    affects: (count) =>
      `Changing the temperature will affect the execution behavior of the following ${prompts(count)}:`,
    question: "Would you like to proceed with changing the temperature and review/edit these prompts now?",
    okText: "Change Temperature & Review Prompts",
  },
  effort: {
    title: "Change Reasoning Effort & Review Affected Prompts?",
    affects: (count) =>
      `Changing the reasoning effort will affect the execution behavior of the following ${prompts(count)}:`,
    question: "Would you like to proceed with changing the reasoning effort and review/edit these prompts now?",
    okText: "Change Effort & Review Prompts",
  },
};

export type ChangeSettingKey = keyof typeof CHANGE_SETTING_COPY;

export const HEALTH_TITLE = "Variables in use";

export const SAVE_BLOCKED_TITLE = "Unable to Save Prompt";

export const MISSING_VARIABLES_LABEL = "Missing Required Variable(s):";

export const MISSING_VARIABLES_MESSAGE =
  "The system requires these variables to function properly. Please insert them into your prompt text before saving.";

export const MALFORMED_VARIABLES_LABEL = "The following text looks like a variable but is not one:";

export const MALFORMED_VARIABLES_MESSAGE =
  "A variable is {{name}} — two braces on each side, and letters, digits or underscores in between. The prompt engine refuses anything else, so please correct or delete these before saving.";

export const SAVE_BLOCKED_BY_MALFORMED = "Cannot save: malformed variables.";

export const UNKNOWN_VARIABLES_LABEL =
  "The following variable(s) are not recognized or supported by the prompt parser:";

export const UNKNOWN_VARIABLES_MESSAGE = "Please delete these variables from your text and try saving again.";

export const SAVE_BLOCKED_BY_MISSING = "Cannot save: Missing required variables.";

export const SAVE_BLOCKED_BY_UNKNOWN = "Cannot save: unrecognised variables.";

export const SAVE_BLOCKED_BY_EMPTY = "Cannot save: the prompt is empty.";

export const SAVE_NOTHING_CHANGED = "No changes to save.";

export const SAVE_BUTTON = "Save as Language-Specific";

export const RESET_BUTTON = "Reset to Default";

export const RESET_ALREADY_DEFAULT = "Already on the system default text.";

export const RESET_ALREADY_PUBLIC = "Already on the published text.";

export const RESET_TITLE = "Reset to Default?";

export const RESET_OK_TEXT = "Reset to Default";

export const RESET_MESSAGE =
  "This restores the system default text for this prompt. Your changes to its text will be gone — the model and temperature are left as they are.";

export const RESET_PUBLIC_BUTTON = "Reset to Public";

export const RESET_PUBLIC_TITLE = "Reset to Public?";

export const RESET_PUBLIC_OK_TEXT = "Reset to Public";

export const RESET_PUBLIC_MESSAGE =
  "This restores the text this language's published version is running for this prompt. Your changes to its text will be gone — the model and temperature are left as they are.";

// 🔴 A reset writes the working copy like a save does, so the backend drops a publish-ready language's status.
export const RESET_REVERT_OK_TEXT = "Reset & Revert to Preview";

export const SAVE_SUCCESS = (promptName: string) => `${promptName} saved.`;

export const RESET_SUCCESS = (promptName: string) => `${promptName} is back on the system default text.`;

export const RESET_PUBLIC_SUCCESS = (promptName: string) => `${promptName} is back on the published text.`;

export const LEAVE_TITLE = "Leave without saving?";

export const LEAVE_MESSAGE = "All changes will be lost if you leave this page without saving.";

export const LEAVE_OK_TEXT = "Leave";

export const LEAVE_CANCEL_TEXT = "Stay on this page";

export const UNSUPPORTED_MARKDOWN_TITLE = "This prompt contains formatting the editor cannot show";

export const UNSUPPORTED_MARKDOWN_MESSAGE =
  "Saving would remove it, so saving is disabled. Ask an engineer to simplify the prompt in the database, or remove it there first.";

export const SAVE_BLOCKED_BY_UNSUPPORTED = "Cannot save: this prompt contains formatting the editor cannot show.";
